import React, { useEffect } from 'react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { track } from '@nexus/fff-analytics';

import { useUserContext } from '@nexus/fff-utilities';
import Layout from '../../components/Layout';
import MyBox from '../../components/MyBox';
import NotFoundError from '../NotFoundError';
import HeroBanner from '../../components/HeroBanner';
import useHeroBanner from './hooks/useHeroBanner';
import InvitePopup from '../../components/InvitePopup';
import useWebCards from './hooks/useWebCards';
import useScreeMode from '../../hooks/useScreenMode';
import useMyBox from './hooks/useMyBox';
import useSeasonalTimelines from './hooks/useSeasonalTimelines';
import useInviteTotal from './hooks/useInviteTotal';
import useShowInvitePopup from './hooks/useShowInvitePopup';
import useLaunchDarkly from '../../hooks/useLaunchDarkly';
import WebCards from '../../components/WebCards';
import SeasonalTimeline from '../../components/SeasonalTimeline';
import {
  isUnavailableStatus, isNotSubscribedStatus, isExpired, isHomepageLoading,
} from './helpers';
import { setLocalStorage } from '../../utils/localStorageHelpers';
import './Homepage.css';

const VIEWED = 'Experiment Viewed';

const Homepage = ({ flags, ldClient, test }) => {
  const { userInfo } = useUserContext();
  useLaunchDarkly(ldClient, userInfo);
  const screenMode = useScreeMode();
  const heroBanner = useHeroBanner();
  const myBox = useMyBox();
  const webCards = useWebCards();
  const seasonalTimelines = useSeasonalTimelines();
  const isSeasonalTimelinesVisible = flags.seasonalTimelineAbTest === true;
  const showReviewButton = myBox.data.eligible && !myBox.data.surveyStarted;
  const isLoading = isHomepageLoading({
    heroBanner, myBox, webCards, seasonalTimelines,
  });
  const inviteTotal = useInviteTotal(flags.homepageCallsNewInvitationApi);
  const { showInvitePopup, stateDispatch } = useShowInvitePopup('invite_popup_exp');
  const closeInvitePopup = () => {
    const invitePopupExpDate = new Date();
    invitePopupExpDate.setDate(invitePopupExpDate.getDate() + 7);
    setLocalStorage('invite_popup_exp', invitePopupExpDate);
    stateDispatch({ type: 'REQUEST_SHOW_INVITE_POPUP_FAIL' });
    document.body.style.overflow = 'auto';
  };

  useEffect(() => {
    if (flags && Object.keys(flags).length > 0 && !test) {
      const variationName = isSeasonalTimelinesVisible ? 'Challenger' : 'Control';
      track(VIEWED, {
        experimentName: 'Homepage Rebuild Launch',
        variationName,
      });
    }
  }, [flags]);

  useEffect(() => {
    if (showInvitePopup && inviteTotal.data > 0) {
      document.body.style.overflow = 'hidden';
    }
  }, [showInvitePopup, inviteTotal]);

  if (flags.newHomepage === false) {
    return <NotFoundError />;
  }

  if (flags.newHomepage === true) {
    return (
      <Layout isLoading={isLoading} redirectIfNoAuth>
        {!heroBanner.fail && Object.keys(heroBanner.data).length > 0 ? (
          <HeroBanner data={heroBanner.data} screenMode={screenMode} />
        ) : (
          <div className="hero_banner_placeholder" />
        )}
        {showInvitePopup && inviteTotal.data > 0 && <InvitePopup closeInvitePopup={closeInvitePopup} />}
        {isSeasonalTimelinesVisible && !seasonalTimelines.fail && Object.keys(seasonalTimelines.data).length > 0 && (
          <SeasonalTimeline
            isExpired={isExpired(myBox)}
            isNotSubscribed={isNotSubscribedStatus(myBox.data.status)}
            data={seasonalTimelines.data}
            screenMode={screenMode}
          />
        )}
        {Object.keys(myBox.data).length > 0 && (
          <MyBox
            data={myBox.data}
            screenMode={screenMode}
            isChallenge={isSeasonalTimelinesVisible}
            hasFailed={myBox.fail || isUnavailableStatus(myBox.data.status)}
            showReviewButton={showReviewButton}
          />
        )}
        {!webCards.fail && Object.keys(webCards.data).length > 0 ? (
          <WebCards data={webCards.data} screenMode={screenMode} />
        ) : (
          <div className="web-cards_placeholder" />
        )}
      </Layout>
    );
  }
  //This is on purpose since the feature flags is completed asynchronously and may be undefined.
  return null;
};

export default withLDConsumer()(Homepage);
